<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useClipboard } from "@vueuse/core"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"
import { CONSENT_REQUEST_TYPE } from "@core/constants"

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        consentRequestId: string
        consentRequestType?: string
        link?: string
        sendBack?: boolean
    }>(),
    {
        consentRequestType: CONSENT_REQUEST_TYPE.CHANGE_OWNER,
        link: "",
        sendBack: false,
    }
)

const emit = defineEmits<{
    done: []
    /** Après envoi réussi de l’e-mail d’invitation / relance */
    sent: []
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title_change_owner: "Demande créée",
            title_sign: "Invitation générée",
            title_share: "Demande de partage créée",
            help: "Vous pouvez copier le lien ci-dessous pour l'envoyer directement au titulaire.",
            notif: "Le titulaire recevra un e-mail avec ce lien pour finaliser la demande.",
            success: "L'e-mail a bien été envoyé.",
            see: "Voir l'aperçu",
            copy: "Copier le lien",
            send: "Envoyer l'e-mail",
            quit: "Terminer",
        },
    },
})

// ================================
// STATE
// ================================

const previewHtml = ref<string | null>(null)
const successMessage = ref("")
const copyDone = ref(false)

const { copy } = useClipboard()

// ================================
// FETCH
// ================================

async function sendInvitationFn(): Promise<void> {
    await consentRequestService.sendConsentRequest(
        props.consentRequestId,
        props.sendBack
    )
    successMessage.value = t("success")
    emit("sent")
}

const {
    fetch: fetchSend,
    isFetching: isSending,
    hasError: hasSendError,
    error: sendError,
} = useFetchWrapper(sendInvitationFn)

async function previewInvitationFn(): Promise<void> {
    const html = await consentRequestService.previewConsentRequest(
        props.consentRequestId
    )
    previewHtml.value = html
}

const {
    fetch: fetchPreview,
    isFetching: isPreviewing,
    hasError: hasPreviewError,
    error: previewError,
} = useFetchWrapper(previewInvitationFn)

watch([hasSendError, hasPreviewError], () => {
    if (hasSendError.value || hasPreviewError.value) {
        previewHtml.value = null
    }
})

// ================================
// COMPUTED
// ================================

const getTitle = computed(() => {
    if (props.consentRequestType === CONSENT_REQUEST_TYPE.SHARE) {
        return t("title_share")
    }
    if (props.consentRequestType === CONSENT_REQUEST_TYPE.CHANGE_OWNER) {
        return t("title_change_owner")
    }
    return t("title_sign")
})

const getErrorMessage = computed(() => {
    const err = sendError.value || previewError.value
    if (!err) return ""
    const withResponse = err as Error & {
        response?: { data?: { message?: string }; status?: number }
    }
    return (
        withResponse?.response?.data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

const hasError = computed(() => hasSendError.value || hasPreviewError.value)

const getCopyLink = computed(() =>
    props.consentRequestType === CONSENT_REQUEST_TYPE.SHARE && props.link
        ? `${props.link}?action=accepted`
        : (props.link ?? "")
)

// ================================
// HANDLERS
// ================================

function onQuit(): void {
    emit("done")
}

function onPreview(): void {
    fetchPreview()
}

function onSend(): void {
    fetchSend()
}

async function onClickCopy(): Promise<void> {
    if (!getCopyLink.value) return
    await copy(getCopyLink.value)
    copyDone.value = true
    setTimeout(() => {
        copyDone.value = false
    }, 1000)
}
</script>

<template>
    <div
        class="source-invitation-change-owner-sent flex flex-col items-center gap-4 w-full"
    >
        <header class="w-full flex flex-col gap-2 text-center">
            <h2
                class="text-lg font-semibold text-gray-800 flex items-center justify-center gap-2"
            >
                {{ getTitle }}
                <BaseIcon icon="pi pi-check" class="text-green-600" />
            </h2>
            <p v-if="!sendBack" class="text-sm text-gray-600">
                {{ t("help") }}
            </p>
            <p class="text-sm text-gray-600">
                {{ t("notif") }}
            </p>
        </header>

        <div
            v-if="!successMessage"
            class="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center justify-items-center py-6 w-full"
        >
            <BaseButton
                :label="t('see')"
                severity="secondary"
                :loading="isPreviewing"
                :disabled="isPreviewing"
                class="w-full sm:justify-self-end"
                @click="onPreview"
            />
            <BaseButton
                :label="t('copy')"
                outlined
                :disabled="!getCopyLink"
                class="w-full sm:w-auto gap-2"
                @click="onClickCopy"
            >
                <BaseIcon
                    :icon="copyDone ? 'pi pi-check' : 'pi pi-copy'"
                    :class="copyDone ? 'text-green-600' : ''"
                />
            </BaseButton>
            <BaseButton
                :label="t('send')"
                :loading="isSending"
                :disabled="isSending"
                class="w-full sm:justify-self-start"
                @click="onSend"
            />
        </div>

        <BaseMessage
            v-if="successMessage"
            severity="success"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ successMessage }}
        </BaseMessage>

        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ getErrorMessage }}
        </BaseMessage>

        <div
            v-if="previewHtml"
            class="w-full relative rounded border border-gray-200 overflow-hidden"
        >
            <iframe
                :srcdoc="previewHtml"
                title="Aperçu du mail"
                class="w-full border-0 min-h-[200px] pointer-events-none"
                scrolling="no"
                @load="
                    (e: Event) => {
                        const iframe = e.target as HTMLIFrameElement
                        if (iframe?.contentWindow?.document?.body) {
                            iframe.style.height =
                                iframe.contentWindow.document.body
                                    .scrollHeight + 'px'
                        }
                    }
                "
            />
        </div>

        <div class="w-full flex justify-center">
            <BaseButton
                :label="t('quit')"
                class="max-w-[15rem] w-full"
                severity="secondary"
                @click="onQuit"
            />
        </div>
    </div>
</template>
